/**
 * TagSelector Component
 *
 * Multi-select tag chips grouped by namespace (diet, origin, meal, other).
 * Supports toggling selection and adding custom tags.
 */

import { useState } from 'react';
import type { FoodTag } from '@/types/food';
import { getTagStyle } from '@/types/food';

interface TagSelectorProps {
  tags: FoodTag[];
  selectedTagIds: number[];
  onChange: (tagIds: number[]) => void;
  onAddTag?: (tagName: string) => void;
}

/**
 * Namespace display labels
 */
const NAMESPACE_LABELS: Record<string, string> = {
  diet: 'Diet',
  origin: 'Origin',
  meal: 'Meal',
  course: 'Course',
  season: 'Season',
  other: 'Other',
};

/**
 * Group tags by namespace
 */
function groupTagsByNamespace(tags: FoodTag[]): Record<string, FoodTag[]> {
  const groups: Record<string, FoodTag[]> = {};

  tags.forEach((tag) => {
    const namespace = tag.namespace || 'other';
    if (!groups[namespace]) {
      groups[namespace] = [];
    }
    groups[namespace].push(tag);
  });

  return groups;
}

/**
 * Namespace order for display
 */
const NAMESPACE_ORDER = ['diet', 'origin', 'meal', 'course', 'season', 'other'];

/**
 * Plus icon for add button
 */
function PlusIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

export function TagSelector({
  tags,
  selectedTagIds,
  onChange,
  onAddTag,
}: TagSelectorProps) {
  const [customTagInput, setCustomTagInput] = useState('');

  const toggleTag = (tagId: number) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  const handleAddTag = () => {
    const trimmed = customTagInput.trim();
    if (trimmed && onAddTag) {
      onAddTag(trimmed);
      setCustomTagInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const groupedTags = groupTagsByNamespace(tags);
  const namespaces = NAMESPACE_ORDER.filter((ns) => groupedTags[ns]?.length > 0);

  return (
    <div className="flex flex-col gap-3">
      {namespaces.map((namespace) => (
        <div key={namespace} className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-500">
            {NAMESPACE_LABELS[namespace] || namespace}
          </span>
          <div className="flex flex-wrap gap-2">
            {groupedTags[namespace].map((tag) => {
              const style = getTagStyle(tag.namespace);
              const isSelected = selectedTagIds.includes(tag.id);

              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all border-2 ${
                    isSelected
                      ? `${style.activeBg} text-white border-transparent`
                      : `${style.bg} ${style.text} border-transparent hover:border-current`
                  }`}
                >
                  {tag.value}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Custom tag input */}
      {onAddTag && (
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            value={customTagInput}
            onChange={(e) => setCustomTagInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add custom tag (e.g., season:summer)"
            className="flex-1 max-w-[250px] px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
          />
          <button
            type="button"
            onClick={handleAddTag}
            disabled={!customTagInput.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <PlusIcon />
            Add Tag
          </button>
        </div>
      )}
    </div>
  );
}
